/**
 * Internal analytics — sends events to our Supabase analytics_events table for
 * the /admin dashboard. Runs alongside Meta/TikTok/GA. Fire-and-forget, batched.
 */

import { hasOptedOut } from "./consent";
import { canTrackCartAbandoned } from "./cartAbandonment";
import { fallbackElementLabel, fallbackSectionLabel } from "./analyticsLabels";
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;


// Cookieless: session id lives only in memory for the lifetime of this tab.
// Reloading the tab = new session. No cookies.
let MEMORY_SESSION_ID: string | null = null;
function getSessionId(): string {
  if (typeof window === "undefined") return "";
  if (!MEMORY_SESSION_ID) {
    MEMORY_SESSION_ID =
      (crypto as Crypto).randomUUID?.() ??
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
  return MEMORY_SESSION_ID;
}

// --- Visitor + first-touch attribution -----------------------------------
// First-party localStorage only. No PII, no cross-site tracking. Used purely
// to attribute the visitor to the source they ORIGINALLY arrived from, so
// later sessions (back from checkout etc.) don't overwrite the real source.
// If the user opts out (CCPA), we skip storage and fall back to per-session.
const VID_KEY = "oaria_vid";
const FT_KEY = "oaria_ft";

function safeLS(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    // Probe — Safari private mode can throw.
    window.localStorage.setItem("__t", "1");
    window.localStorage.removeItem("__t");
    return window.localStorage;
  } catch {
    return null;
  }
}

function userOptedOut(): boolean {
  try { return hasOptedOut(); } catch { return false; }
}


let MEMORY_VID: string | null = null;
function getVisitorId(): string | null {
  if (typeof window === "undefined") return null;
  if (MEMORY_VID) return MEMORY_VID;
  if (userOptedOut()) return null;
  const ls = safeLS();
  if (!ls) return null;
  let vid = ls.getItem(VID_KEY);
  if (!vid) {
    vid =
      (crypto as Crypto).randomUUID?.() ??
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try { ls.setItem(VID_KEY, vid); } catch { /* ignore */ }
  }
  MEMORY_VID = vid;
  return vid;
}

interface FirstTouch {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  referrer?: string;
  landing_path?: string;
  click_id?: string;
}

let MEMORY_FT: FirstTouch | null = null;
function getFirstTouch(): FirstTouch {
  if (typeof window === "undefined") return {};
  if (MEMORY_FT) return MEMORY_FT;
  const ls = safeLS();
  // Try persisted first-touch
  if (ls && !userOptedOut()) {
    const raw = ls.getItem(FT_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as FirstTouch;
        MEMORY_FT = parsed;
        return parsed;
      } catch { /* fall through */ }
    }
  }
  // Build from current URL — this is the visitor's true first touch.
  try {
    const url = new URL(window.location.href);
    const ref = document.referrer || "";
    // Treat same-origin referrer (back from checkout etc.) as no-referrer for first-touch.
    let referrer: string | undefined;
    try {
      if (ref) {
        const rh = new URL(ref).hostname.toLowerCase();
        const isSelf = /(^|\.)oariaskin\.com$/.test(rh) || rh === window.location.hostname;
        referrer = isSelf ? undefined : ref;
      }
    } catch { referrer = ref || undefined; }
    const ft: FirstTouch = {
      utm_source: url.searchParams.get("utm_source") || undefined,
      utm_medium: url.searchParams.get("utm_medium") || undefined,
      utm_campaign: url.searchParams.get("utm_campaign") || undefined,
      referrer,
      landing_path: url.pathname || undefined,
    };
    // Detect ad click IDs even without UTM — promote to source AND keep raw id.
    const fbclid = url.searchParams.get("fbclid");
    const ttclid = url.searchParams.get("ttclid");
    const gclid = url.searchParams.get("gclid");
    const msclkid = url.searchParams.get("msclkid");
    if (fbclid) { ft.click_id = `fbclid:${fbclid}`; if (!ft.utm_source) ft.utm_source = "meta"; }
    else if (ttclid) { ft.click_id = `ttclid:${ttclid}`; if (!ft.utm_source) ft.utm_source = "tiktok"; }
    else if (gclid) { ft.click_id = `gclid:${gclid}`; if (!ft.utm_source) ft.utm_source = "google"; }
    else if (msclkid) { ft.click_id = `msclkid:${msclkid}`; if (!ft.utm_source) ft.utm_source = "bing"; }
    MEMORY_FT = ft;
    if (ls && !userOptedOut()) {
      try { ls.setItem(FT_KEY, JSON.stringify(ft)); } catch { /* ignore */ }
    }
    return ft;
  } catch {
    return {};
  }
}

interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  referrer?: string;
}

let MEMORY_ATTR: Attribution | null = null;
const SS_UTM_KEY = "oaria_sess_utm";
function getAttribution(): Attribution {
  if (typeof window === "undefined") return {};
  if (MEMORY_ATTR) return MEMORY_ATTR;
  try {
    const url = new URL(window.location.href);
    let utm_source = url.searchParams.get("utm_source") || undefined;
    let utm_medium = url.searchParams.get("utm_medium") || undefined;
    let utm_campaign = url.searchParams.get("utm_campaign") || undefined;
    // sessionStorage backup — Shopify checkout strips UTMs on return navigation.
    // If current URL has UTMs, persist them for the rest of this tab session.
    // If not, fall back to the backup so the second pageview isn't "direct".
    try {
      const ss = window.sessionStorage;
      if (utm_source || utm_medium || utm_campaign) {
        ss.setItem(SS_UTM_KEY, JSON.stringify({ utm_source, utm_medium, utm_campaign }));
      } else {
        const raw = ss.getItem(SS_UTM_KEY);
        if (raw) {
          const b = JSON.parse(raw) as Attribution;
          utm_source = utm_source || b.utm_source;
          utm_medium = utm_medium || b.utm_medium;
          utm_campaign = utm_campaign || b.utm_campaign;
        }
      }
    } catch { /* sessionStorage may be unavailable */ }
    MEMORY_ATTR = {
      utm_source,
      utm_medium,
      utm_campaign,
      referrer: document.referrer || undefined,
    };
    return MEMORY_ATTR;
  } catch {
    return {};
  }
}



interface TrackEvent {
  event_name: string;
  path?: string;
  product_id?: string;
  product_name?: string;
  value?: number;
  currency?: string;
  num_items?: number;
  scroll_pct?: number;
  active_ms?: number;
  element_text?: string;
  element_href?: string;
  element_label?: string;
  section?: string;
  element_tag?: string;
  element_class?: string;
  element_id?: string;
  element_src?: string;
  viewport_w?: number;
  viewport_h?: number;
  pos_x_pct?: number;
  pos_y_pct?: number;
  pos_x_doc_pct?: number;
  pos_y_doc_pct?: number;
}

// --- Batched sender ---
const queue: Record<string, unknown>[] = [];
let flushTimer: number | null = null;
const FLUSH_MS = 2000;
const MAX_BATCH = 10;

function flush(useBeacon = false) {
  if (typeof window === "undefined" || !SUPABASE_URL || queue.length === 0) return;
  const events = queue.splice(0, queue.length);
  const url = `${SUPABASE_URL}/functions/v1/analytics-track`;
  const body = JSON.stringify({ events });
  if (useBeacon && "sendBeacon" in navigator) {
    try {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    } catch { /* fall through */ }
  }
  try {
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_ANON_KEY || "",
        Authorization: `Bearer ${SUPABASE_ANON_KEY || ""}`,
      },
      body,
      keepalive: true,
    }).catch(() => { /* ignore */ });
  } catch { /* ignore */ }
}

function enqueue(event: TrackEvent, immediate = false) {
  if (typeof window === "undefined") return;
  if (window.location.pathname.startsWith("/admin")) return;
  if (window.self !== window.top) return;
  const attr = getAttribution();
  const ft = getFirstTouch();
  queue.push({
    session_id: getSessionId(),
    visitor_id: getVisitorId(),
    event_name: event.event_name,
    path: event.path ?? window.location.pathname,
    ...attr,
    first_utm_source: ft.utm_source,
    first_utm_medium: ft.utm_medium,
    first_utm_campaign: ft.utm_campaign,
    first_referrer: ft.referrer,
    first_landing_path: ft.landing_path,
    first_click_id: ft.click_id,

    product_id: event.product_id,
    product_name: event.product_name,
    value: event.value,
    currency: event.currency,
    num_items: event.num_items,
    scroll_pct: event.scroll_pct,
    active_ms: event.active_ms,
    section: event.section,
    element_tag: event.element_tag,
    element_class: event.element_class,
    element_id: event.element_id,
    element_src: event.element_src,
    element_text: event.element_text,
    element_href: event.element_href,
    element_label: event.element_label,
    viewport_w: event.viewport_w,
    viewport_h: event.viewport_h,
    pos_x_pct: event.pos_x_pct,
    pos_y_pct: event.pos_y_pct,
    pos_x_doc_pct: event.pos_x_doc_pct,
    pos_y_doc_pct: event.pos_y_doc_pct,
  });
  if (immediate || queue.length >= MAX_BATCH) {
    if (flushTimer !== null) { clearTimeout(flushTimer); flushTimer = null; }
    flush();
    return;
  }
  if (flushTimer === null) {
    flushTimer = window.setTimeout(() => { flushTimer = null; flush(); }, FLUSH_MS);
  }
}

// --- Public trackers ---
export function trackPageView(path?: string) {
  resetPageTimers();
  enqueue({ event_name: "page_view", path });
}

export function trackViewContent(params: { product_id: string; product_name: string; value?: number; currency?: string }) {
  enqueue({ event_name: "view_content", ...params });
}

export function trackAddToCart(params: { product_id: string; product_name: string; value: number; currency: string; num_items?: number; element_label?: string }) {
  enqueue({ event_name: "add_to_cart", ...params }, true);
}

export function trackVariantSelect(params: { product_id?: string; product_name?: string; element_label: string; value?: number; currency?: string }) {
  enqueue({ event_name: "variant_select", ...params });
}

export function trackCustomEvent(name: string, params: Partial<TrackEvent> = {}) {
  enqueue({ event_name: name, ...params });
}


export function trackInitiateCheckout(params: { value: number; currency: string; num_items: number }) {
  checkoutInitiated = true;
  enqueue({ event_name: "initiate_checkout", ...params }, true);
}

let cartItemsCount = 0;
let cartAbandonmentSent = false;
let checkoutInitiated = false;
export function setCartItemsForAbandonment(count: number) {
  cartItemsCount = Math.max(0, Math.trunc(count));
}
export function trackCartAbandoned() {
  if (!canTrackCartAbandoned({ itemCount: cartItemsCount, sent: cartAbandonmentSent, checkoutInitiated })) return;
  cartAbandonmentSent = true;
  enqueue({ event_name: "cart_abandoned", num_items: cartItemsCount }, true);
}
// --- Engagement listeners (scroll depth, active time, clicks) ---
let listenersAttached = false;
let scrollMarks: Set<number> = new Set();
let activeStart = 0;
let activeAccum = 0;
let lastPath = "";

function resetPageTimers() {
  if (typeof window === "undefined") return;
  // flush accumulated time for the previous page
  if (lastPath && (activeAccum > 0 || activeStart > 0)) {
    const now = Date.now();
    const total = activeAccum + (activeStart ? now - activeStart : 0);
    if (total >= 500) {
      enqueue({
        event_name: "time_on_page",
        path: lastPath,
        active_ms: Math.min(total, 30 * 60_000),
        scroll_pct: getScrollPct(),
      });
    }
  }
  scrollMarks = new Set();
  activeAccum = 0;
  activeStart = document.visibilityState === "visible" ? Date.now() : 0;
  lastPath = window.location.pathname;
}

function getScrollPct(): number {
  const h = document.documentElement;
  const scrolled = (h.scrollTop || document.body.scrollTop);
  const max = (h.scrollHeight - h.clientHeight) || 1;
  return Math.min(100, Math.max(0, Math.round((scrolled / max) * 100)));
}
function getElementContext(el: HTMLElement) {
  const labelled = el.closest<HTMLElement>("[data-track]");
  const sectioned = el.closest<HTMLElement>("[data-section]");
  const sectionRoot = el.closest<HTMLElement>("section") || sectioned;
  const contextRoot = sectionRoot || el.parentElement;
  const headingAncestor = el.closest<HTMLElement>("h1,h2,h3,h4,h5,h6");
  const link = el.closest<HTMLElement>("a,button");
  const text = (link?.innerText || el.innerText || "").trim().slice(0, 120);
  const href = (link as HTMLAnchorElement | null)?.href || undefined;
  const tag = el.tagName.toLowerCase();
  const cls = (el.className?.toString() || "").trim().slice(0, 80) || undefined;
  const id = el.id || undefined;
  const src = (el as HTMLImageElement).src || (el as HTMLImageElement).currentSrc || undefined;
  const heading = headingAncestor?.innerText || contextRoot?.querySelector<HTMLElement>("h1,h2,h3,h4,h5,h6")?.innerText;
  return {
    element_text: text || undefined,
    element_href: href,
    element_label: fallbackElementLabel({
      track: labelled?.dataset.track,
      aria: link?.getAttribute("aria-label"),
      text,
      tag,
    }),
    section: fallbackSectionLabel({
      dataSection: sectioned?.dataset.section,
      heading,
      path: window.location.pathname,
    }),
    element_tag: tag,
    element_class: cls,
    element_id: id,
    element_src: src,
  };
}

const HEARTBEAT_MS = 15_000;
let heartbeatTimer: number | null = null;

function flushActiveTime(reason: "heartbeat" | "hide" | "pagehide") {
  const now = Date.now();
  if (activeStart) {
    activeAccum += now - activeStart;
    activeStart = reason === "heartbeat" ? now : 0;
  }
  if (activeAccum >= 500) {
    enqueue({
      event_name: "time_on_page",
      active_ms: Math.min(activeAccum, 30 * 60_000),
      scroll_pct: getScrollPct(),
    });
    activeAccum = 0;
  }
}

function startHeartbeat() {
  if (typeof window === "undefined" || heartbeatTimer !== null) return;
  heartbeatTimer = window.setInterval(() => {
    if (document.visibilityState !== "visible") return;
    flushActiveTime("heartbeat");
  }, HEARTBEAT_MS);
}

function stopHeartbeat() {
  if (heartbeatTimer !== null) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

export function initEngagementTracking() {
  if (typeof window === "undefined" || listenersAttached) return;
  listenersAttached = true;
  lastPath = window.location.pathname;
  activeStart = document.visibilityState === "visible" ? Date.now() : 0;
  if (activeStart) startHeartbeat();

  // Scroll depth
  let rafQueued = false;
  const onScroll = () => {
    if (rafQueued) return;
    rafQueued = true;
    requestAnimationFrame(() => {
      rafQueued = false;
      const pct = getScrollPct();
      for (const mark of [25, 50, 75, 100]) {
        if (pct >= mark && !scrollMarks.has(mark)) {
          scrollMarks.add(mark);
          enqueue({ event_name: "scroll_depth", scroll_pct: mark });
        }
      }
    });
  };
  window.addEventListener("scroll", onScroll, { passive: true });

  // Active time
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushActiveTime("hide");
      trackCartAbandoned();
      stopHeartbeat();
    } else {
      activeStart = Date.now();
      startHeartbeat();
    }
  });
  window.addEventListener("pagehide", () => {
    flushActiveTime("pagehide");
    trackCartAbandoned();
    stopHeartbeat();
    flush(true);
  });


  // Click tracking — dedup within 250ms per element
  let lastClickKey = "";
  let lastClickAt = 0;
  document.addEventListener("click", (ev) => {
    const target = ev.target as HTMLElement | null;
    if (!target) return;
    const interactive = target.closest("a,button,[data-track]");
    const ctx = getElementContext(target);
    const isDead = !interactive;
    const key = (ctx.element_label || ctx.element_text || "") + "|" + (ctx.element_href || "");
    const now = Date.now();
    if (key === lastClickKey && now - lastClickAt < 250) return;
    lastClickKey = key;
    lastClickAt = now;

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const pct = (value: number, total: number) => Math.max(0, Math.min(100, Math.round((value / Math.max(total, 1)) * 1000) / 10));
    const x = pct(ev.clientX, vw);
    const y = pct(ev.clientY, vh);
    const doc = document.documentElement;
    const pageW = Math.max(doc.scrollWidth, document.body?.scrollWidth || 0, vw);
    const pageH = Math.max(doc.scrollHeight, document.body?.scrollHeight || 0, vh);
    const xDoc = pct(ev.pageX, pageW);
    const yDoc = pct(ev.pageY, pageH);
    enqueue({
      event_name: isDead ? "dead_click" : "element_click",
      ...ctx,
      viewport_w: vw,
      viewport_h: vh,
      pos_x_pct: x,
      pos_y_pct: y,
      pos_x_doc_pct: xDoc,
      pos_y_doc_pct: yDoc,
    });
  }, { capture: true, passive: true });
}

export function initErrorTracking() {
  if (typeof window === "undefined") return;
  window.addEventListener("error", (ev) => {
    const msg = ev.message?.slice(0, 200) || "Unknown error";
    const filename = ev.filename?.split("/").pop()?.slice(0, 50) || "";
    const lineno = ev.lineno || 0;
    enqueue({
      event_name: "js_error",
      element_text: `${msg} (${filename}:${lineno})`,
    }, true);
  });
  window.addEventListener("unhandledrejection", (ev) => {
    const reason = ev.reason;
    const msg = (typeof reason === "string" ? reason : reason?.message || String(reason))?.slice(0, 200) || "Unhandled promise rejection";
    enqueue({
      event_name: "js_error",
      element_text: msg,
    }, true);
  });
}
